<template>
  <div class="dialogue">
    <div class="greeting-area">
      <GreetingArea isShowRgt></GreetingArea>
    </div>
    <div class="dialogue-area" ref="scrollableContainer">
      <AIChatting
        v-for="(item, index) in treatmentList"
        ref="chatItems"
        :key="`${item.type}-${index}-${item.id || ''}`"
        :cardData="item"
        hideInlineConfirm
        @taskCompleted="continueWorkflowAfterTask({ forceAdvance: true })"
      ></AIChatting>
    </div>
    <div v-if="agentSending" class="reply-pending" role="status">正在回复你...</div>
    <div class="input-area">
      <transition name="task-dock">
        <button
          v-if="currentIncompleteTask"
          class="daily-task-dock"
          :disabled="interactionBusy"
          @click="openCurrentTask"
        >
          <span class="daily-task-mark">今日任务</span>
          <span class="daily-task-copy">
            <strong>{{ currentTaskTitle }}</strong>
            <small>建议优先完成，也可以先和我聊聊</small>
          </span>
          <span class="daily-task-action">去完成</span>
        </button>
        <div v-else-if="workflowAdvancing" class="workflow-reminder">
          正在为你准备今天的练习...
        </div>
        <button v-else-if="workflowError" class="workflow-reminder" @click="initTreatmentFn">
          今天的任务暂未加载成功，点击重试
        </button>
        <div v-else-if="dailyNotice" class="workflow-reminder">{{ dailyNotice }}</div>
      </transition>
      <div class="composer-row">
        <textarea
          v-model="inputText"
          class="input-box"
          :placeholder="inputPlaceholder"
          :disabled="agentSending"
          rows="1"
        ></textarea>
        <button class="send-btn" :disabled="interactionBusy || !trimmedInput" @click="sendText">
          {{ agentSending ? '发送中' : '发送' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { v4 as uuidv4 } from "uuid";
import GreetingArea from "@/components/GreetingArea/GreetingArea.vue";
import AIChatting from "@/components/AIChatting/AIChatting.vue";
import { streamAgent } from "@/api/agent";
import { calendarDayKey, clearDialogueTaskEntry, findTaskComponent, shouldRefreshDailyContext } from "@/utils/taskNavigation";
import { CHAT_LIST } from "@/consts";

export default {
  name: "Dialogue",
  components: {
    GreetingArea,
    AIChatting,
  },
  data() {
    return {
      conversationId: uuidv4(),
      inputText: "",
      inputPlaceholder: "请输入你的想法，建议使用语音输入法",
      agentSending: false,
      workflowAdvancing: false,
      workflowError: false,
      dailyNotice: '',
      allowedTaskIds: [],
      pendingTaskRefresh: false,
      pendingForceAdvance: false,
      initialized: false,
      preparedDate: '',
    };
  },
  computed: {
    ...mapState({
      treatmentList: (state) => state.dialogue.treatmentList,
      treatmentCode: (state) => state.dialogue.treatmentCode,
      treatmentInstanceId: (state) => state.dialogue.id,
    }),
    hasIncompleteTask() {
      return Boolean(this.currentIncompleteTask);
    },
    currentTaskTitle() {
      const task = this.currentIncompleteTask;
      return (task && CHAT_LIST[task.item_type]?.title) || '今天的练习已经准备好';
    },
    currentIncompleteTask() {
      for (let index = 0; index < this.treatmentList.length; index += 1) {
        const item = this.treatmentList[index];
        if (this.isIncompleteTask(item)) {
          return item;
        }
      }
      return null;
    },
    incompleteTaskIds() {
      return this.treatmentList
        .filter((item) => item && item.id != null && this.isIncompleteTask(item))
        .map((item) => String(item.id));
    },
    trimmedInput() {
      return this.inputText.trim();
    },
    interactionBusy() {
      return this.agentSending || this.workflowAdvancing;
    },
  },
  mounted() {
    clearDialogueTaskEntry();
    this.initTreatmentFn();
    document.addEventListener('visibilitychange', this.refreshIfDayChanged);
    window.addEventListener('focus', this.refreshIfDayChanged);
    this.dailyContextTimer = setInterval(this.refreshIfDayChanged, 60000);
  },
  beforeDestroy() {
    if (this.agentStream) this.agentStream.close();
    clearTimeout(this.agentTimeout);
    clearInterval(this.dailyContextTimer);
    document.removeEventListener('visibilitychange', this.refreshIfDayChanged);
    window.removeEventListener('focus', this.refreshIfDayChanged);
  },
  activated() {
    clearDialogueTaskEntry();
    if (this.initialized && !this.treatmentInstanceId) {
      this.initialized = false;
      this.initTreatmentFn();
    } else if (this.initialized) {
      this.continueWorkflowAfterTask();
    }
  },
  methods: {
    refreshIfDayChanged() {
      // The client date only triggers revalidation; the server still owns the treatment day.
      if (this.initialized && !document.hidden && this.$route.path === '/layout/dialogue' &&
        this.preparedDate !== calendarDayKey()) this.continueWorkflowAfterTask();
    },
    ...mapActions([
      "initTreatment",
      "getTreatment",
      "prepareDailyTask",
    ]),
    async initTreatmentFn() {
      if (this.workflowAdvancing) return;
      this.workflowAdvancing = true;
      this.workflowError = false;
      try {
        const resData = await this.initTreatment();
        if (!resData) throw new Error('Treatment initialization failed');
        const task = await this.prepareDailyTask();
        this.acceptDailyResponse(task);
        const history = await this.getTreatmentFn();
        this.workflowError = task === false || history === false;
        this.initialized = true;
      } catch (error) {
        this.workflowError = true;
      } finally {
        this.workflowAdvancing = false;
      }
    },
    acceptDailyResponse(response) {
      if (response) this.preparedDate = calendarDayKey();
      const items = response?.step_item_type === 'LIST' ? response.step_items || [] : [response?.step_item];
      this.allowedTaskIds = items.filter(item => item?.id != null && !['guide_language', 'SYS_INFO'].includes(item.item_type))
        .map(item => String(item.id));
      this.dailyNotice = response?.step_item?.item_type === 'SYS_INFO'
        ? response.step_item.settings?.content || '' : '';
    },
    async getTreatmentFn() {
      const resData = await this.getTreatment();
      if (resData) {
        this.scrollToBottom();
      }
      return resData;
    },
    async continueWorkflowAfterTask({ forceAdvance = false } = {}) {
      if (this.interactionBusy) {
        this.pendingTaskRefresh = true;
        this.pendingForceAdvance = this.pendingForceAdvance || forceAdvance;
        return;
      }
      const shouldForceAdvance = forceAdvance || this.pendingForceAdvance;
      this.pendingTaskRefresh = false;
      this.pendingForceAdvance = false;
      this.workflowAdvancing = true;
      this.workflowError = false;
      try {
        const pendingBeforeRefresh = [...this.incompleteTaskIds];
        const resData = await this.getTreatmentFn();
        if (resData === false) {
          this.workflowError = true;
          return;
        }
        const pendingAfterRefresh = new Set(this.incompleteTaskIds);
        const completedWhileAway = pendingBeforeRefresh.some(
          (id) => !pendingAfterRefresh.has(id)
        );
        if (shouldRefreshDailyContext({ hasIncompleteTask: this.hasIncompleteTask,
          preparedDate: this.preparedDate, currentDate: calendarDayKey(),
          forceRefresh: shouldForceAdvance || completedWhileAway })) {
          const task = await this.prepareDailyTask();
          this.acceptDailyResponse(task);
          const history = await this.getTreatmentFn();
          this.workflowError = task === false || history === false;
        }
      } finally {
        this.workflowAdvancing = false;
        this.flushTaskRefresh();
      }
    },
    flushTaskRefresh() {
      if (this.pendingTaskRefresh && !this.interactionBusy && this.$route.path === '/layout/dialogue') {
        this.continueWorkflowAfterTask();
      }
    },
    isIncompleteTask(item) {
      return Boolean(
        item &&
        this.allowedTaskIds.includes(String(item.id)) &&
        item.item_type &&
        item.item_type !== "guide_language" &&
        item.item_type !== "user_input" &&
        item.item_type !== "SYS_INFO" &&
        item.status !== "COMPLETED" &&
        item.status !== "CANCELLED"
      );
    },
    openCurrentTask() {
      const task = this.currentIncompleteTask;
      if (!task || !this.$refs.scrollableContainer) {
        return;
      }
      const taskIndex = this.treatmentList.lastIndexOf(task);
      const component = findTaskComponent(this.$refs.chatItems, task);
      if (component) {
        component.openTask();
        return;
      }
      const taskElement = this.$refs.scrollableContainer.children[taskIndex];
      if (taskElement && taskElement.scrollIntoView) {
        taskElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    async sendText() {
      const text = this.trimmedInput;
      if (!text || this.interactionBusy) {
        return;
      }
      this.inputText = "";
      this.agentSending = true;
      this.appendTreatmentItems([{
        type: "ChatSend",
        text,
        isShowTime: true,
      }]);
      this.scrollToBottom();

      const sse = streamAgent(this.treatmentCode, this.treatmentInstanceId, {
        message: text,
        conversationId: this.conversationId,
        clientMessageId: uuidv4(),
      });
      this.agentStream = sse;
      this.agentTimeout = setTimeout(() => {
        sse.close();
        this.agentSending = false;
        this.flushTaskRefresh();
        this.$toast('回复等待较久，请稍后重试');
      }, 60000);
      sse.addEventListener("message", (event) => {
        let resJson = {};
        try {
          resJson = JSON.parse(event.data);
        } catch (error) {
          resJson = {};
        }
        if (resJson.code !== 200) {
          this.agentSending = false;
          this.flushTaskRefresh();
          clearTimeout(this.agentTimeout);
          this.$toast(resJson.msg || "Agent 响应失败");
          sse.close();
          return;
        }
        if (resJson.finished) {
          this.applyStructuredResult(resJson);
          this.agentSending = false;
          this.flushTaskRefresh();
          clearTimeout(this.agentTimeout);
          sse.close();
        }
      });
      sse.addEventListener("error", () => {
        this.agentSending = false;
        this.flushTaskRefresh();
        clearTimeout(this.agentTimeout);
        this.$toast("Agent 暂时不可用，请稍后再试");
        sse.close();
      });
      sse.stream();
    },
    applyStructuredResult(resJson) {
      const result = resJson.data || {};
      const assistantText = resJson.content || this.joinText(result.prefaceGuide, result.reply);
      const nextItems = [];
      if (assistantText) {
        nextItems.push({
          type: "ChatText",
          text: assistantText,
          plainText: true,
          item_type: "guide_language",
          cardMode: "dialogue",
          isShowTime: true,
        });
      }
      if (result.recommendedCard && !this.hasRenderedCard(result.recommendedCard)) {
        nextItems.push(this.convertAgentCard(result.recommendedCard));
      }
      this.appendTreatmentItems(nextItems);
      this.scrollToBottom();
    },
    appendTreatmentItems(items) {
      if (!items.length) {
        return;
      }
      this.$store.commit("setTreatmentList", [
        ...this.treatmentList,
        ...items,
      ]);
    },
    convertAgentCard(card) {
      const itemTypeList = this.$store.state.itemTypeList || {};
      return {
        id: card.id,
        item_type: card.itemType,
        type: itemTypeList[card.itemType] || "ChatAnswerSheet",
        settings: card.settings || {},
        cardMode: "dialogue",
        isShowTime: true,
        status: card.status || "INITIATED",
      };
    },
    hasRenderedCard(card) {
      return this.treatmentList.some((item) => (
        item &&
        item.id != null &&
        String(item.id) === String(card.id) &&
        item.item_type === card.itemType
      ));
    },
    joinText(prefaceGuide, reply) {
      if (!prefaceGuide) {
        return reply || "";
      }
      if (!reply) {
        return prefaceGuide;
      }
      return `${prefaceGuide}\n\n${reply}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.scrollableContainer) {
            this.$refs.scrollableContainer.scrollTop = this.$refs.scrollableContainer.scrollHeight;
          }
        }, 100);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.dialogue {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.greeting-area {
  flex-shrink: 0;
  width: 100%;
  height: 103px;
  padding: 0 20px;
  box-sizing: border-box;
  background: #fff;
}

.dialogue-area {
  min-height: 0;
  padding: 0 24px 18px;
  box-sizing: border-box;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
}

.input-area {
  flex-shrink: 0;
  padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.97);
  border-top: 1px solid #e8eef6;
  box-shadow: 0 -8px 24px rgba(64, 91, 128, 0.06);
}
.reply-pending {
  padding: 7px 24px;
  color: #68839a;
  font-size: 12px;
}

.daily-task-dock {
  width: 100%;
  min-height: 56px;
  margin-bottom: 9px;
  padding: 9px 10px;
  border: 1px solid #a9cfee;
  border-radius: 14px;
  background: linear-gradient(135deg, #edf7ff 0%, #dff0fb 100%);
  color: #28425f;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  text-align: left;
  box-shadow: 0 5px 16px rgba(63, 126, 176, 0.1);
}

.daily-task-dock:active {
  transform: translateY(1px);
}

.daily-task-dock:disabled {
  opacity: 0.6;
}

.daily-task-mark {
  padding: 5px 8px;
  border-radius: 9px;
  background: #4e88b8;
  color: #fff;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.daily-task-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.daily-task-copy strong {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
}

.daily-task-copy small {
  color: #68839a;
  font-size: 10px;
  line-height: 15px;
}

.daily-task-action {
  color: #3977a7;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.workflow-reminder {
  min-height: 38px;
  margin-bottom: 9px;
  padding: 9px 12px;
  box-sizing: border-box;
  border: 1px solid #d7e6f3;
  border-radius: 12px;
  background: #f5f9fc;
  color: #52718a;
  font-size: 12px;
  line-height: 18px;
}

.composer-row {
  display: flex;
  align-items: flex-end;
  gap: 9px;
}

.input-box {
  flex: 1;
  min-width: 0;
  height: 44px;
  min-height: 44px;
  max-height: 88px;
  padding: 11px 13px;
  box-sizing: border-box;
  border: 1px solid #dbe3ec;
  border-radius: 12px;
  background: #fbfcfe;
  resize: none;
  color: #26384b;
  font-size: 13px;
  line-height: 21px;
  outline: none;
}

.input-box:focus {
  border-color: #86bde7;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(134, 189, 231, 0.14);
}

.input-box::placeholder {
  color: #9aa8b6;
}

.send-btn {
  flex: 0 0 64px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f88d8 0%, #3972c4 100%);
  box-shadow: 0 5px 12px rgba(57, 114, 196, 0.2);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.send-btn:disabled {
  background: #cbd5df;
  box-shadow: none;
  opacity: 0.8;
}

@media (max-width: 360px) {
  .input-box {
    height: 56PX;
    min-height: 56PX;
    padding: 8PX 10PX;
    font-size: 14PX;
    line-height: 20PX;
  }
}

.task-dock-enter-active,
.task-dock-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.task-dock-enter,
.task-dock-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
